import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

interface Branding {
  primaryColor: string;
  secondaryColor: string;
  logoUrl?: string;
  organizationName?: string;
}

interface BrandingContextType {
  branding: Branding | null;
  loadBranding: (organizerId: string) => Promise<void>;
  resetBranding: () => void;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

const defaultBranding: Branding = {
  primaryColor: '#2563EB',  // Calm blue accent
  secondaryColor: '#2563EB', // Same as primary for consistency
  logoUrl: '/logo.png',
  organizationName: 'Sykkelfest',
};

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [branding, setBranding] = useState<Branding>(defaultBranding);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const applyBranding = (brandingData: Branding) => {
    // Only override accent color - keep neutral palette intact
    document.documentElement.style.setProperty('--color-accent', brandingData.primaryColor);
    
    // Legacy compatibility
    document.documentElement.style.setProperty('--primary-color', brandingData.primaryColor);
    document.documentElement.style.setProperty('--secondary-color', brandingData.primaryColor);
    document.documentElement.style.setProperty('--color-primary', brandingData.primaryColor);
    document.documentElement.style.setProperty('--color-secondary', brandingData.primaryColor);
  };

  const loadBranding = async (organizerId: string) => {
    try {
      const response = await axios.get(`${API_URL}/public/organizer/${organizerId}`);
      if (response.data.isWhiteLabelEnabled && response.data.branding) {
        setBranding(response.data.branding);
        applyBranding(response.data.branding);
      }
    } catch (error) {
      console.error('Failed to load branding:', error);
    }
  };

  const resetBranding = () => {
    setBranding(defaultBranding);
    applyBranding(defaultBranding);
  };

  useEffect(() => {
    applyBranding(branding);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, loadBranding, resetBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within BrandingProvider');
  }
  return context;
};
